import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, CRICKET_API_URL } from "../../config";

const SETTLEABLE_STATUSES = ["completed", "abandoned", "cancelled", "canceled"];

const normalizeStatus = (value = "") => String(value || "").trim().toLowerCase();

const isSettleableStatus = (value = "") =>
  SETTLEABLE_STATUSES.includes(normalizeStatus(value));

const getMatchRowId = (match = {}) =>
  String(match._id || match.matchId || match.id || "").trim();

const getMatchSettleId = (match = {}) =>
  String(match.matchId || match.id || match._id || "").trim();

const getMatchDisplayName = (match = {}) => {
  const explicitName = String(match.name || match.matchTitle || "").trim();
  if (explicitName) return explicitName;

  const homeName =
    match.homeTeam?.name || match.team1?.name || match.team1 || "Team 1";
  const awayName =
    match.awayTeam?.name || match.team2?.name || match.team2 || "Team 2";

  return `${homeName} vs ${awayName}`;
};

const getStatusBadgeClass = (status = "") => {
  const normalized = normalizeStatus(status);

  if (normalized === "completed") return "bg-success-subtle text-success";
  if (normalized === "abandoned") return "bg-danger-subtle text-danger";
  if (normalized === "cancelled" || normalized === "canceled") {
    return "bg-warning-subtle text-warning";
  }

  return "bg-secondary-subtle text-secondary";
};

const formatMatchDate = (match = {}) => {
  const rawValue =
    match.matchStartTime ||
    match.startTime ||
    match.startTimestamp ||
    match.createdAt;

  if (!rawValue) return "TBD";

  const parsedDate =
    typeof rawValue === "number"
      ? new Date(rawValue < 10000000000 ? rawValue * 1000 : rawValue)
      : new Date(rawValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "TBD";
  }

  return parsedDate.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const normalizeMatchesResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.matches)) return payload.matches;
  if (Array.isArray(payload?.events)) return payload.events;
  return [];
};

function MatchSettlements() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchMatchesNeedingSettlement();
  }, []);

  const fetchMatchesNeedingSettlement = async () => {
    setLoading(true);

    try {
      const today = new Date().toISOString().split("T")[0];
      let fetchedMatches = [];

      try {
        const directRes = await axios.get(
          `${CRICKET_API_URL}/api/cricket/matches/upcoming`
        );
        fetchedMatches = normalizeMatchesResponse(directRes.data);
      } catch (upcomingError) {
        const fallbackRes = await axios.get(
          `${CRICKET_API_URL}/api/cricket/matches/${today}`
        );
        fetchedMatches = normalizeMatchesResponse(fallbackRes.data);
      }

      setMatches(
        fetchedMatches.filter((match) => isSettleableStatus(match.status))
      );
    } catch (error) {
      console.error("Match settlements fetch error:", error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSettleMatch = async (match = {}) => {
    const rowId = getMatchRowId(match);
    const settleId = getMatchSettleId(match);

    if (!settleId) {
      window.alert("Unable to identify this match for settlement.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to distribute prizes/refunds for this match?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setProcessingId(rowId);

      const token =
        localStorage.getItem("adminToken") || localStorage.getItem("token");

      await axios.post(
        `${API_URL}/api/admin/match/${settleId}/settle`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      window.alert("Match settled successfully.");

      setMatches((currentMatches) =>
        currentMatches.map((currentMatch) =>
          getMatchRowId(currentMatch) === rowId
            ? { ...currentMatch, settled: true }
            : currentMatch
        )
      );
    } catch (error) {
      console.error("Match settlement error:", error);
      window.alert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to settle this match."
      );
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="container-fluid p-4 fade-in-up">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Match Settlements</h2>
          <p className="text-muted mb-0">
            Review completed matches and trigger prize distribution or refunds.
          </p>
        </div>

        <button
          className={`btn border shadow-sm rounded-pill px-4 fw-bold ${
            loading ? "btn-secondary text-white" : "btn-light text-dark"
          }`}
          onClick={fetchMatchesNeedingSettlement}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Updating...
            </>
          ) : (
            <>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </>
          )}
        </button>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
        <div className="card-header bg-white border-bottom py-3 px-4">
          <h6 className="fw-bold text-dark mb-0">
            <i className="bi bi-trophy-fill me-2 text-warning"></i>
            Settlement Queue
          </h6>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="px-4 py-3 text-uppercase small text-muted border-0">
                  Match Name
                </th>
                <th className="py-3 text-uppercase small text-muted border-0">
                  Date / Time
                </th>
                <th className="py-3 text-uppercase small text-muted border-0">
                  Status
                </th>
                <th className="py-3 text-uppercase small text-muted border-0">
                  Settlement Status
                </th>
                <th className="px-4 py-3 text-uppercase small text-muted border-0 text-end">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    <td className="px-4 py-4">
                      <div className="placeholder-glow">
                        <span className="placeholder col-8 rounded"></span>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="placeholder-glow">
                        <span className="placeholder col-7 rounded"></span>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6 rounded"></span>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="placeholder-glow">
                        <span className="placeholder col-6 rounded"></span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-end">
                      <div className="placeholder-glow">
                        <span className="placeholder col-8 rounded"></span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : matches.length > 0 ? (
                matches.map((match) => {
                  const rowId = getMatchRowId(match);
                  const isSettled = Boolean(match.settled);
                  const isProcessing = processingId === rowId;
                  const displayStatus = normalizeStatus(match.status);

                  return (
                    <tr key={rowId || getMatchDisplayName(match)}>
                      <td className="px-4 py-3">
                        <div className="fw-bold text-dark">
                          {getMatchDisplayName(match)}
                        </div>
                        <small className="text-muted">
                          ID: {getMatchSettleId(match) || "N/A"}
                        </small>
                      </td>

                      <td className="py-3 text-muted">
                        {formatMatchDate(match)}
                      </td>

                      <td className="py-3">
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold ${getStatusBadgeClass(
                            displayStatus
                          )}`}
                        >
                          {displayStatus === "canceled"
                            ? "Cancelled"
                            : displayStatus
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                        </span>
                      </td>

                      <td className="py-3">
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold ${
                            isSettled
                              ? "bg-success-subtle text-success"
                              : "bg-warning-subtle text-warning"
                          }`}
                        >
                          {isSettled ? "Settled" : "Pending"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-end">
                        {isSettled ? (
                          <button
                            className="btn btn-success btn-sm rounded-pill px-3 fw-semibold"
                            disabled
                          >
                            ✅ Settled
                          </button>
                        ) : (
                          <button
                            className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                              displayStatus === "completed"
                                ? "btn-primary"
                                : "btn-warning text-dark"
                            }`}
                            onClick={() => handleSettleMatch(match)}
                            disabled={isProcessing}
                          >
                            {isProcessing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing...
                              </>
                            ) : (
                              "Distribute Prize & Settle"
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="text-muted">
                      No completed or abandoned matches are currently available
                      for settlement.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .fade-in-up {
          animation: fadeInUp 0.5s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default MatchSettlements;
